/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from "@emotion/core";
import { useI18n } from "hooks/i18n/i18n";
import { TRANSLATE_THIS_BLOCK_BY_WEGLOT } from "i18n/weglot";
import React from "react";

import { startDatetimeAndEndDatetime } from "./utils";

import type { TicketData } from "@spwn/types/firebase/database";

type Props = {
  ticket: TicketData;
  onPurchaseClick: (ticketId: string) => void;
};

export const TicketCard: React.FC<Props> = ({ ticket, onPurchaseClick }) => {
  const { t, i18n } = useI18n();
  const classes = styles();
  const currentLanguage = i18n.language;

  return (
    <article css={classes.ticketCard}>
      <header css={classes.ticketCardHeader}>
        <h3 css={classes.ticketCardTitle}>{ticket.name}</h3>
        <dl css={classes.ticketInfo}>
          <dt css={classes.ticketInfoLabel}>
            {t("event.saleInfo.ticket.ticketSalesPeriod")}
          </dt>
          <dd css={classes.ticketInfoValue}>
            {startDatetimeAndEndDatetime(
              ticket.releaseDateTime,
              ticket.closeDateTime,
              currentLanguage
            )}
          </dd>
        </dl>
      </header>

      <div css={classes.ticketCardBody}>
        <div css={classes.ticketPrice}>
          <span css={classes.priceLabel}>
            {t("event.archiveVideo.sellingPrice")}
          </span>
          <div css={classes.ticketPrice}>
            <span css={classes.priceAmount}>
              ¥{ticket.price_jpy.toLocaleString()}
            </span>
            <span css={classes.priceTax}>{t("ui.goodsCard.tax")}</span>
          </div>
        </div>
        <button
          css={classes.ticketPurchaseButton}
          onClick={() => onPurchaseClick(ticket._id)}
        >
          {t("common.routes.ticketPurchase")}
        </button>

        <div
          css={classes.ticketDescription}
          className={TRANSLATE_THIS_BLOCK_BY_WEGLOT}
          dangerouslySetInnerHTML={{ __html: ticket.description }}
        />
      </div>
    </article>
  );
};

const styles = () => {
  return {
    ticketCard: css`
      background-color: #fff;
      border: 2px solid #f0f0f0;
      border-radius: 16px;
    `,
    ticketCardHeader: css`
      background-color: rgba(0, 0, 0, 0.04);
      padding: 16px;
      border-radius: 16px 16px 0 0;
    `,
    ticketCardTitle: css`
      font-size: 16px;
      font-weight: bold;
      color: black;
      line-height: 1.5;
      margin: 0;
      margin-bottom: 8px;
    `,
    ticketCardBody: css`
      padding: 16px;
      padding-bottom: 24px;
      display: flex;
      flex-direction: column;
      gap: 16px;
      flex: 1;
    `,
    ticketInfo: css`
      margin: 0;
    `,
    ticketInfoLabel: css`
      font-size: 12px;
      font-weight: 600;
      color: #1e1e1e;
      margin-bottom: 4px;
    `,
    ticketInfoValue: css`
      font-size: 14px;
      color: #1e1e1e;
      margin: 0;
      line-height: 1.5;
    `,
    ticketPrice: css`
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 4px;
    `,
    priceLabel: css`
      font-size: 14px;
      font-weight: 600;
      color: #666;
    `,
    priceAmount: css`
      font-size: 24px;
      font-weight: bold;
      color: black;
    `,
    priceTax: css`
      font-size: 12px;
      color: #666;
    `,
    ticketCardFooter: css`
      padding-top: 16px;
      border-top: 1px solid #e0e0e0;
    `,
    ticketPurchaseButton: css`
      width: 100%;
      padding: 12px 20px;
      font-size: 16px;
      font-weight: 600;
      color: #fff;
      background-color: black;
      border: none;
      border-radius: 6px;
      cursor: pointer;
      transition: background-color 0.2s;
      &:hover {
        background-color: #333;
      }
      &:active {
        background-color: #000;
      }
    `,
    ticketDescription: css`
      color: #000;
      font-size: 12px;
      font-style: normal;
      font-weight: 500;
      line-height: 16px; /* 133.333% */
    `,
  };
};
